import Vue from "vue";
import { Module, Action } from "vuex";
import pick from "lodash/pick";
import cloneDeep from "lodash/cloneDeep";
import axios from "axios";
import { SimplePoster, Poster, simplePosterKeys } from "../models/poster";
import { StoreState } from "./index";
import { doWork } from "./shared";
import { showAlert, hideAlert } from "./content";
import {
  posterService,
  PosterType,
  PosterUploadsResult,
  EvalPreinscripcionForm,
  EvaluacionForm,
} from "../services/poster";
import { downloadBlob, makeTimeId } from "../util";

interface PosterLocales {
  tipoLocale?: string;
  presentacionLocale?: string;
}

interface UploadsUpdate {
  type?: PosterType;
  poster: SimplePosterWithLocales;
  file: File;
  url_video: string;
}

interface PreincripcionUpdate {
  type?: PosterType;
  form: EvalPreinscripcionForm;
  poster: PosterWithLocales;
}

interface EvaluacionUpdate {
  type?: PosterType;
  form: EvaluacionForm;
  poster: PosterWithLocales;
}

export type SimplePosterWithLocales = SimplePoster & PosterLocales;
export type PosterWithLocales = Poster & PosterLocales;

export interface PosterModuleState {
  posters: Record<PosterType, SimplePosterWithLocales[] | null>;
  posterCache: { [key: string]: PosterWithLocales };
}

function addPosterLocales(rootGetters: any, p: SimplePosterWithLocales) {
  const getLocale = rootGetters["data/getLocale"];
  p.tipoLocale = getLocale("tipo", p.tipo);
  p.presentacionLocale = getLocale("presentacion", p.tipo_aceptacion);
}

const upsertPosterAction = (
  serviceMethod: "addPoster" | "updatePoster"
): Action<PosterModuleState, StoreState> => (
  context,
  { type = "user", poster }: { type: PosterType; poster: Poster }
) =>
  doWork(context, `posterUpsert[${poster.id_poster}]`, async () => {
    const { commit, rootGetters } = context;
    const result: PosterWithLocales = await posterService[serviceMethod](
      poster
    );
    addPosterLocales(rootGetters, result);
    commit("pushPoster", { type, poster: result });
  });

const initialState: PosterModuleState = {
  posters: {
    user: null,
    eval: null,
    all: null,
  },
  posterCache: {},
};

const posterModule: Module<PosterModuleState, StoreState> = {
  namespaced: true,
  state: cloneDeep(initialState),

  getters: {
    poster: (state) => ({
      type,
      id,
    }: {
      type: PosterType;
      id: string | number;
    }) => {
      const items = state.posters[type];
      if (!items) {
        return null;
      }

      return items.find((p) => String(p.id_poster) === String(id)) || null;
    },
  },

  mutations: {
    setPosters(
      state,
      {
        type,
        posters,
      }: { type: PosterType; posters: SimplePosterWithLocales[] }
    ) {
      Vue.set(state.posters, type, posters);
    },

    clearPosterData(state) {
      Object.assign(state, cloneDeep(initialState));
    },

    pushPoster(
      state,
      { type, poster }: { type: PosterType; poster: PosterWithLocales }
    ) {
      let posters = state.posters[type];
      const index = posters
        ? posters.findIndex((p) => p.id_poster === poster.id_poster)
        : -1;

      if (!posters) {
        posters = [];
        state.posters[type] = posters;
      }

      const push = index === -1;
      if (push) {
        poster.index = posters.length;
      }

      const simplePoster = pick(poster, [...simplePosterKeys, "tipoLocale"]);

      if (push) {
        posters.push(simplePoster);
      } else {
        posters.splice(index, 1, simplePoster);
      }

      state.posterCache[poster.id_poster] = poster;
    },

    sortPosters(
      state,
      {
        type,
        sortBy,
        sortDesc,
      }: {
        type: PosterType;
        sortBy: (keyof SimplePoster)[];
        sortDesc: boolean[];
      }
    ) {
      const posters = state.posters[type];
      if (!posters) {
        return;
      }

      if (!sortBy.includes("codigo")) {
        sortBy = sortBy.concat("codigo");
        sortDesc = sortDesc.concat(false);
      }

      posters.sort((a, b) => {
        let result = 0;
        sortBy.some((prop, index) => {
          const u = a[prop];
          const v = b[prop];
          if (u === v) {
            return false;
          }
          result = String(u).localeCompare(String(v));
          if (sortDesc[index]) {
            result *= -1;
          }
          return true;
        });
        return result;
      });

      posters.forEach((p, i) => {
        p.index = i;
        if (p.id_poster in state.posterCache) {
          state.posterCache[p.id_poster].index = i;
        }
      });
    },

    cachePoster(state, poster: PosterWithLocales) {
      state.posterCache[poster.id_poster] = poster;
    },
  },

  actions: {
    fetchPosters: (context, type: PosterType) =>
      doWork(context, `fetchPosters[${type}]`, async () => {
        const { commit, rootGetters } = context;
        let posters: SimplePosterWithLocales[] | null = null;
        hideAlert(commit);

        try {
          posters = await posterService.get(type);
        } catch (err) {
          commit("setPosters", { type, posters: null });
          showAlert(commit, {
            type: "error",
            message: "No fue posible obtener el listado",
          });
          return;
        }

        posters.forEach((item, index) => {
          item.index = index;
          addPosterLocales(rootGetters, item);
        });
        commit("setPosters", { type, posters });
      }),

    getDetailedPoster: (
      context,
      {
        type,
        poster: simplePoster,
        reload,
      }: { type: PosterType; poster: SimplePoster; reload?: boolean }
    ) =>
      doWork(context, `posterDetails[${simplePoster.id_poster}]`, async () => {
        const { state, commit, rootGetters } = context;
        const id = simplePoster.id_poster;
        let poster: Poster;

        if (!reload && id in state.posterCache) {
          return state.posterCache[id];
        }

        try {
          poster = await posterService.getPoster(id, type);
        } catch (err) {
          return null;
        }

        poster.index = simplePoster.index;
        addPosterLocales(rootGetters, poster);
        commit("cachePoster", poster);
        return poster;
      }),

    addPoster: upsertPosterAction("addPoster"),
    updatePoster: upsertPosterAction("updatePoster"),

    sendPosterUploads: (
      context,
      { type = "user", poster, file, url_video }: UploadsUpdate
    ) =>
      doWork(context, `posterUploads[${poster.id_poster}]`, async () => {
        const { state, commit } = context;
        let result: PosterUploadsResult;
        hideAlert(commit);

        try {
          result = await posterService.posterUploads({
            id_poster: poster.id_poster,
            file,
            url_video,
          });
        } catch (err) {
          if (err instanceof axios.Cancel) {
            return;
          }

          showAlert(commit, {
            type: "error",
            message: "No fue posible realizar la actualización",
          });

          throw err;
        }

        const { index } = poster;
        const posters = state.posters[type];

        if (!posters || typeof index !== "number") {
          return;
        }

        const update = pick(result, ["url_video", "file"]) as any;
        update._vp = Boolean(
          result.file && (poster.tipo_aceptacion !== "PST" || result.url_video)
        );
        Object.assign(poster, update);

        if (poster.id_poster in state.posterCache) {
          Object.assign(state.posterCache[poster.id_poster], update);
        }

        showAlert(commit, {
          type: "success",
          message: `${poster.presentacionLocale} actualizado`,
        });

        return poster;
      }),

    evalPreinscripcionPoster: (
      context,
      { type = "eval", form, poster }: PreincripcionUpdate
    ) =>
      doWork(
        context,
        `posterEvalPreinscripcion[${poster.id_poster}]`,
        async () => {
          const { state } = context;
          try {
            await posterService.evalPreinscripcionPoster({
              ...form,
              estado: poster.estado,
            });
          } catch (err) {
            return;
          }
          const { index } = poster;
          if (!state.posters || typeof index !== "number") {
            return;
          }
          const revision = form.preaceptado ? "PA" : "PR";
          const simplePoster = state.posters[type]?.[index];
          if (simplePoster) {
            Object.assign(simplePoster, { revision, evaluacion: form });
          }

          Object.assign(poster, { revision, evaluacion: form });
        }
      ),

    evaluacionPoster: (
      context,
      { type = "eval", form, poster }: EvaluacionUpdate
    ) =>
      doWork(context, `posterEvaluacion[${poster.id_poster}]`, async () => {
        const { state } = context;

        await posterService.evaluacionPoster(form);

        const { index } = poster;
        if (!state.posters || typeof index !== "number") {
          throw new Error("Poster no valido");
        }

        const revision = "RD";
        const simplePoster = state.posters[type]?.[index];
        const update = {
          revision,
          evaluacion: form,
          calificado: true,
        };

        if (simplePoster) {
          Object.assign(simplePoster, update);
        }

        Object.assign(poster, update);
        return `${poster.presentacionLocale} evaluado`;
      }),

    downloadPosterPdf: (context, poster: Poster) =>
      doWork(context, "downloadPosterPdf", async () => {
        const response = await posterService.pdfFromPoster(poster);
        const blob = new Blob([response.data], { type: "application/pdf" });
        downloadBlob(blob, `CACIED-2023-${poster?.codigo}_${makeTimeId()}.pdf`);
      }),
  },
};

export default posterModule;
